import type { Grid, PublishGridRequest } from "../../api";
import { formatTokens, parseTokenInput, toBigInt } from "../../format";

/**
 * Une ligne de la grille en cours d'édition, telle que l'ESN la saisit. Le
 * tarif reste la chaîne tapée, en tokens : il n'est converti en centièmes
 * qu'à la validation, pour qu'une saisie à moitié tapée ne soit pas réécrite
 * sous les doigts.
 */
export type LigneEdition = {
  /** Clé React stable, sans valeur métier. */
  cle: string;
  /**
   * Code de l'expertise. Vide pour une ligne ajoutée : il est alors tiré du
   * libellé. Une ligne reprise de la grille garde le sien, pour que la même
   * expertise porte le même code d'une version à l'autre.
   */
  code: string;
  libelle: string;
  tarif: string;
};

export type ErreursLigne = { libelle?: string; tarif?: string };

export type ResultatEdition =
  | { ok: true; requete: PublishGridRequest }
  | { ok: false; libelle?: string; globale?: string; lignes: Record<string, ErreursLigne> };

/** Reprend la grille en vigueur comme point de départ de l'édition. */
export function lignesDepuisGrille(grille: Grid): LigneEdition[] {
  return grille.expertises.map((e) => ({
    cle: e.id,
    code: e.code,
    libelle: e.label,
    // Sans espace de groupement : la saisie se relit telle quelle.
    tarif: formatTokens(e.tokens_per_day).replace(/[\s\u202f\u00a0]/g, ""),
  }));
}

/**
 * Tire un code stable d'un libellé : minuscules, sans accent, mots joints par
 * « _ ». « Agent IA & data » devient « agent_ia_data ».
 */
export function codeDepuisLibelle(libelle: string): string {
  return libelle
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

/**
 * Valide l'édition et, si tout tient, construit le corps de la publication,
 * préparée sur la grille `versionBase` : le serveur refuse (409) si une autre
 * version a été publiée entre-temps.
 * Les règles sont celles du back — libellé présent, tarif strictement positif
 * et pair en centièmes, codes uniques — dites ici pour que l'erreur s'affiche
 * sur la ligne fautive plutôt qu'en un refus global du serveur.
 */
export function validerEdition(versionBase: number, libelleGrille: string, lignes: LigneEdition[]): ResultatEdition {
  const erreurs: Record<string, ErreursLigne> = {};
  const libelle = libelleGrille.trim();
  const erreurLibelle = libelle === "" ? "Donnez un nom à cette version de la grille." : undefined;

  if (lignes.length === 0) {
    return { ok: false, libelle: erreurLibelle, globale: "La grille doit compter au moins une expertise.", lignes: erreurs };
  }

  const codes = new Map<string, string>();
  let globale: string | undefined;
  const expertises: PublishGridRequest["expertises"] = [];

  for (const ligne of lignes) {
    const e: ErreursLigne = {};
    const lib = ligne.libelle.trim();
    if (lib === "") {
      e.libelle = "Libellé obligatoire.";
    }
    const centiemes = parseTokenInput(ligne.tarif);
    if (centiemes === null || toBigInt(centiemes) <= 0n) {
      e.tarif = "Tarif en tokens par jour, deux décimales au plus.";
    } else if (toBigInt(centiemes) % 2n !== 0n) {
      e.tarif = "Le tarif doit tomber juste à la demi-journée : un nombre pair de centièmes.";
    }
    const code = ligne.code !== "" ? ligne.code : codeDepuisLibelle(lib);
    if (lib !== "" && code === "") {
      e.libelle = "Le libellé doit contenir au moins une lettre ou un chiffre.";
    }
    const deja = codes.get(code);
    if (code !== "" && deja !== undefined) {
      e.libelle = `Même code que « ${deja} » : changez l'un des deux libellés.`;
    }
    codes.set(code, lib);
    if (e.libelle !== undefined || e.tarif !== undefined) {
      erreurs[ligne.cle] = e;
      globale = "Corrigez les lignes signalées avant de publier.";
      continue;
    }
    expertises.push({ code, label: lib, tokens_per_day: centiemes as string });
  }

  if (erreurLibelle !== undefined || globale !== undefined) {
    return { ok: false, libelle: erreurLibelle, globale, lignes: erreurs };
  }
  return { ok: true, requete: { base_version: versionBase, label: libelle, expertises } };
}
